import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { submitContact } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export const Contact = ({ settings }) => {
  const { lang } = useLang();
  const hi = lang === "hi";
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const email = settings?.contact_email || "info@findivine.org";
  const phone = settings?.phone || "+91-8875285537";
  const address = settings?.address || null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error(hi ? "कृपया नाम, ईमेल और संदेश भरें।" : "Please fill name, email and message.");
    setLoading(true);
    try {
      await submitContact(form);
      toast.success(hi ? "धन्यवाद! हम जल्द ही संपर्क करेंगे। 🙏" : "Thank you! We'll be in touch soon. 🙏");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error(hi ? "कुछ गलत हुआ। पुनः प्रयास करें।" : "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-primary mb-5">{hi ? "संपर्क में रहें" : "Reach out to us"}</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground leading-[1.05] mb-8">
            {hi ? "आपके प्रश्न, हमारी प्राथमिकता।" : "Questions? We're here to help."}
          </h2>
          <div className="space-y-5">
            <a href={`mailto:${email}`} className="flex items-center gap-4 text-foreground/80 hover:text-primary transition-colors">
              <span className="w-11 h-11 rounded-full border border-border flex items-center justify-center"><Mail size={17} /></span>
              <span className="text-lg">{email}</span>
            </a>
            <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-4 text-foreground/80 hover:text-primary transition-colors">
              <span className="w-11 h-11 rounded-full border border-border flex items-center justify-center"><Phone size={17} /></span>
              <span className="text-lg">{phone}</span>
            </a>
            {address && (
              <div className="flex items-center gap-4 text-foreground/80">
                <span className="w-11 h-11 rounded-full border border-border flex items-center justify-center"><MapPin size={17} /></span>
                <span className="text-lg">{address}</span>
              </div>
            )}
          </div>
        </div>

        <motion.form
          onSubmit={submit} data-testid="contact-form"
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="bg-card border border-border p-7 sm:p-9 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input data-testid="contact-name" placeholder={hi ? "नाम" : "Name"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/60" />
            <Input data-testid="contact-phone" placeholder={hi ? "फ़ोन" : "Phone"} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/60" />
          </div>
          <Input data-testid="contact-email" type="email" placeholder={hi ? "ईमेल" : "Email"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/60" />
          <Input data-testid="contact-subject" placeholder={hi ? "विषय" : "Subject"} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-white/60" />
          <textarea data-testid="contact-message" rows={4} placeholder={hi ? "आपका संदेश" : "Your message"} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full rounded-md border border-input bg-white/60 px-3 py-2 text-base md:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
          <button data-testid="contact-submit" disabled={loading}
            className="w-full rounded-full bg-primary text-primary-foreground py-3.5 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:scale-[1.01] transition-transform disabled:opacity-60">
            {loading && <Loader2 size={16} className="animate-spin" />} {hi ? "संदेश भेजें" : "Send message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
